import { useCallback, useState } from "react";
import { FileSpreadsheet } from "lucide-react";

import Button from "../components/ui/Button";
import VendorSearch from "../components/payouts/VendorSearch";
import TimeRangePicker from "../components/ui/TimeRangePicker";
import PayoutSummary from "../components/payouts/PayoutSummary";
import CurrencyRates from "../components/payouts/CurrencyRates";
import OrdersTable from "../components/payouts/OrdersTable";
import useAsyncData from "../hooks/useAsyncData";
import { getVendorPayouts } from "../utils/api";
import { getPresetRange } from "../utils/dateRanges";
import { isoDay } from "../utils/format";
import { buildPayoutsCsv, downloadCsv } from "../utils/csv";

export default function PayoutsPage() {
  const [vendor, setVendor] = useState(null);
  const [range, setRange] = useState({ preset: "30d", ...getPresetRange("30d") });
  const [currency, setCurrency] = useState("GBP");
  const [rate, setRate] = useState(1);

  const { data, loading, error } = useAsyncData(
    useCallback(
      () => getVendorPayouts(vendor.id, { start: range.start, end: range.end }),
      [vendor, range]
    ),
    [vendor, range],
    { enabled: Boolean(vendor), errorMessage: "Failed to load payout data." }
  );

  function handleSelectVendor(selected) {
    setVendor(selected);
    setRange({ preset: "30d", ...getPresetRange("30d") });
    setCurrency("GBP");
    setRate(1);
  }

  function handleBack() {
    setVendor(null);
    setCurrency("GBP");
    setRate(1);
  }

  function handleExportCsv() {
    if (!data) return;

    // Always GBP, whatever the screen is showing — see the note in utils/csv.
    const csv = buildPayoutsCsv({
      vendorName: vendor.name,
      range,
      summary: data.summary,
      orders: data.orders,
    });

    const safeVendor = vendor.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    downloadCsv(
      `${safeVendor}-payouts-${isoDay(range.start)}-to-${isoDay(range.end)}.csv`,
      csv
    );
  }

  if (!vendor) {
    return <VendorSearch onSelect={handleSelectVendor} />;
  }

  return (
    <div className="w-full flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-4xl font-bold tracking-tight">{vendor.name}</h2>
          <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray mt-2">
            Payout overview
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={handleBack}>
            ← All Vendors
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <TimeRangePicker value={range} onChange={setRange} />
        <div className="flex items-stretch gap-4">
          <CurrencyRates
            currency={currency}
            rate={rate}
            onCurrencyChange={setCurrency}
            onRateChange={setRate}
          />
          <Button
            variant="secondary"
            onClick={handleExportCsv}
            disabled={!data}
            className="h-full"
          >
            <span className="flex flex-col items-center justify-center gap-4 h-full">
              Export CSV
              <FileSpreadsheet className="h-5 w-5" />
            </span>
          </Button>
        </div>
      </div>

      {loading && (
        <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray">
          Loading payout data...
        </p>
      )}

      {error && (
        <p className="font-mono text-xs uppercase tracking-widest text-red-600">{error}</p>
      )}

      {!loading && !error && data && (
        <>
          <PayoutSummary summary={data.summary} range={range} currency={currency} rate={rate} />
          <OrdersTable orders={data.orders} currency={currency} rate={rate} />
        </>
      )}
    </div>
  );
}